/**
 * /accounts/:id/buckets — list buckets on an S3 account with pinned metadata
 * (alias, public URL, notes). Visual follows the Lovable design brief.
 */

import { type FormEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, api } from '../lib/api';
import { Badge, Banner, Icon } from '../components/brand';

interface Bucket {
  name: string;
  alias: string | null;
  publicBaseUrl: string | null;
  publicSource: string | null;
  publicDetectedAt: number | null;
  publicEnabled: boolean;
  notes: string | null;
}

interface AccountSummary {
  id: string;
  name: string;
  provider: string;
  endpoint: string | null;
  region: string;
}

export function Buckets() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const accountQ = useQuery({
    queryKey: ['account', id],
    queryFn: () => api.get<AccountSummary>(`/api/accounts/${id}`),
    enabled: !!id,
  });

  const bucketsQ = useQuery({
    queryKey: ['accounts', id, 'buckets'],
    queryFn: () => api.get<Bucket[]>(`/api/accounts/${id}/buckets`),
    enabled: !!id,
  });

  const [expanded, setExpanded] = useState<string | null>(null);
  const account = accountQ.data;
  const buckets = bucketsQ.data ?? [];

  return (
    <div data-screen-label="04 Buckets">
      <button
        type="button"
        className="bm-btn bm-btn-sm"
        style={{
          color: 'var(--bm-text-muted)',
          padding: '0 0 12px',
          marginBottom: 12,
        }}
        onClick={() => navigate('/accounts')}
      >
        <Icon.arrowLeft size={12} /> All accounts
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <div>
          {account && (
            <div className="bm-row bm-gap-12" style={{ marginBottom: 6 }}>
              <Badge variant="accent">{account.provider.toUpperCase()}</Badge>
              {account.endpoint && (
                <span className="mono" style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
                  {account.endpoint}
                </span>
              )}
            </div>
          )}
          <h1 className="h-page">{account?.name ?? id}</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            {bucketsQ.isLoading
              ? 'Loading buckets…'
              : `${buckets.length} buckets${account ? ` · region ${account.region}` : ''}`}
          </p>
        </div>
      </div>

      {bucketsQ.error && (
        <div style={{ marginBottom: 16 }}>
          <Banner variant="danger" code={(bucketsQ.error as ApiError).code ?? 'ERROR'}>
            {(bucketsQ.error as Error).message}
          </Banner>
        </div>
      )}

      {!bucketsQ.isLoading && buckets.length === 0 ? (
        <div className="bm-card bm-empty">
          <Icon.bucket size={28} />
          <h3>No buckets on this account.</h3>
          <p className="muted" style={{ fontSize: 13 }}>
            Buckets created in the provider console appear here automatically.
          </p>
        </div>
      ) : (
        <div className="bm-col bm-gap-12">
          {buckets.map((b) => (
            <BucketCard
              key={b.name}
              accountId={id}
              bucket={b}
              expanded={expanded === b.name}
              onToggle={() => setExpanded(expanded === b.name ? null : b.name)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Card ─────────────────────────────────────────────────────────── */

function BucketCard({
  accountId,
  bucket,
  expanded,
  onToggle,
}: {
  accountId: string;
  bucket: Bucket;
  expanded: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();
  const alias = bucket.alias || bucket.name;

  return (
    <div className="bm-card" style={{ padding: 0 }}>
      <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="bm-row bm-gap-8" style={{ flexWrap: 'wrap' }}>
            <h3 className="h-card" style={{ whiteSpace: 'nowrap' }}>
              {alias}
            </h3>
            {bucket.publicEnabled && (
              <Badge variant="success">
                <Icon.globe size={10} /> public
              </Badge>
            )}
            {bucket.alias && bucket.alias !== bucket.name && (
              <span
                className="mono"
                style={{
                  fontSize: 12,
                  color: 'var(--bm-text-faint)',
                  whiteSpace: 'nowrap',
                }}
              >
                {bucket.name}
              </span>
            )}
          </div>
          <div className="bm-row" style={{ marginTop: 6, gap: 10, flexWrap: 'wrap' }}>
            {bucket.publicBaseUrl ? (
              <span
                className="mono"
                style={{
                  fontSize: 12,
                  color: 'var(--bm-text-soft)',
                  whiteSpace: 'nowrap',
                }}
              >
                {bucket.publicBaseUrl}
              </span>
            ) : (
              <span className="muted" style={{ fontSize: 12 }}>
                private
              </span>
            )}
            {bucket.publicSource && (
              <span className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                · via {bucket.publicSource}
              </span>
            )}
          </div>
        </div>
        <div className="bm-row bm-gap-8" style={{ flexShrink: 0 }}>
          <button
            type="button"
            className="bm-btn bm-btn-primary bm-btn-sm"
            onClick={() =>
              navigate(`/accounts/${accountId}/buckets/${encodeURIComponent(bucket.name)}`)
            }
          >
            <Icon.folder size={12} /> Browse
          </button>
          <button type="button" className="bm-btn bm-btn-ghost bm-btn-sm" onClick={onToggle}>
            <Icon.settings size={12} /> Settings
          </button>
        </div>
      </div>
      {expanded && <BucketSettings accountId={accountId} bucket={bucket} />}
    </div>
  );
}

function BucketSettings({ accountId, bucket }: { accountId: string; bucket: Bucket }) {
  const qc = useQueryClient();
  const [alias, setAlias] = useState(bucket.alias ?? '');
  const [publicBaseUrl, setPublicBaseUrl] = useState(bucket.publicBaseUrl ?? '');
  const [notes, setNotes] = useState(bucket.notes ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch<Bucket>(`/api/accounts/${accountId}/buckets/${encodeURIComponent(bucket.name)}`, {
        alias: alias.trim() || null,
        publicBaseUrl: publicBaseUrl.trim() || null,
        notes: notes || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts', accountId, 'buckets'] }),
  });

  const detect = useMutation({
    mutationFn: () =>
      api.post<{
        publicEnabled: boolean;
        publicBaseUrl: string | null;
        publicSource: string | null;
        skipped?: boolean;
      }>(`/api/accounts/${accountId}/buckets/${encodeURIComponent(bucket.name)}/detect-public`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts', accountId, 'buckets'] }),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  const detection = detect.data;
  const detectErr = detect.error as ApiError | null;

  return (
    <form
      onSubmit={submit}
      style={{
        borderTop: '1px solid var(--bm-border-soft)',
        padding: '20px 20px 22px',
        background: 'var(--bm-surface-0)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 14,
          marginBottom: 14,
        }}
      >
        <label className="bm-field">
          <span className="bm-field-label">Alias</span>
          <input
            className="bm-input"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            maxLength={64}
            placeholder="Optional display name"
          />
        </label>
        <label className="bm-field">
          <span className="bm-field-label">Public base URL</span>
          <div className="bm-row bm-gap-8">
            <input
              className="bm-input mono"
              value={publicBaseUrl}
              onChange={(e) => setPublicBaseUrl(e.target.value)}
              placeholder="https://cdn.example.com"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="bm-btn bm-btn-ghost bm-btn-sm"
              style={{ flexShrink: 0 }}
              disabled={detect.isPending}
              onClick={() => detect.mutate()}
            >
              <Icon.refresh size={11} /> {detect.isPending ? 'Detecting…' : 'Detect'}
            </button>
          </div>
          {detection && (
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--bm-text-muted)',
                marginTop: 6,
              }}
            >
              {detection.skipped ? (
                'Skipped — manual override is set.'
              ) : detection.publicEnabled ? (
                <>
                  Found via{' '}
                  <span style={{ color: 'var(--bm-success)' }}>{detection.publicSource}</span>:{' '}
                  {detection.publicBaseUrl}
                </>
              ) : (
                'No public URL detected.'
              )}
            </div>
          )}
          {detectErr && (
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--bm-danger)',
                marginTop: 6,
              }}
            >
              {detectErr.message}
            </div>
          )}
        </label>
      </div>

      <label className="bm-field" style={{ marginBottom: 14 }}>
        <span className="bm-field-label">Notes</span>
        <textarea
          className="bm-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2048}
          placeholder="Anything operators should know…"
        />
      </label>

      {save.error && (
        <div style={{ marginBottom: 12 }}>
          <Banner variant="danger" code={(save.error as ApiError).code ?? 'ERROR'}>
            {(save.error as Error).message}
          </Banner>
        </div>
      )}

      <div className="bm-row bm-gap-8" style={{ justifyContent: 'flex-end' }}>
        <button type="submit" className="bm-btn bm-btn-primary bm-btn-sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
